/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type PageContextType } from '@/next-types';
import { type NextApiRequest } from 'next';
import { type ParsedUrlQuery } from 'querystring';
import { type FormattedContentStackPLP } from '../../interface/models';
import { getContentByContentType } from '../common/getContentByContentType';
import { type ContentStackProductListingPage } from '../../interface';
import { deepFilterAndSegmentContent } from '../../utils/deepFilterAndSegmentContent';
import { LANDING_PAGE_FIELDS } from '../landingPage/getContentStackLandingPageContent';

export const PRODUCT_LISTING_PAGE_FIELDS = [
  'page_type',
  'shelfresultheader.banner.breadcrumb',
  ...LANDING_PAGE_FIELDS.map((field) => `shelfresultheader.${field}`),
  'shelfresultheader.pencil_banner.pencil_banner',
  'shelfpagebody.banner.banner',
  'shelfpagebody.banner_split.banner_split',
  'shelfpagebody.quotient_sponsored_shelf.quotient_sponsored_shelf',
  'shelfpagebody.product_finder.product_finder',
  'shelfpagebody.ymme_content_block.ymme_content_block',
  'shelfpagebody.quotient_sponsored_search.quotient_sponsored_search',
  'shelfpagebody.results_list.results_list',
  'shelfpagebody.super_duper_hero',
  'shelfpagebody.campaign.campaign',
  'shelfpagebody.null_result.null_result',
  'shelfpagebody.citrus_ad.citrus_ad',
  'shelfpagebody.target_div.target_div',
  'shelfpagebody.experience_injector.experience_injector',
  'shelfpagebody.ad.citrus_ad',
  'shelfpagebody.ad.az_banner',
  'shelfpagebody.image_category_grid.image_category_grid',
  'shelfpagebody.product_chart.product_chart',
  'shelfpagebody.product_grid.product_grid',
  'shelfpagebody.category_grid.category_grid',
  'shelfpagebody.blogtype_grid.blogtype_grid',
  'shelfpagebody.buy_again_container.buy_again_container',
  'shelfresultbottom.certona.certona',
  'shelfresultbottom.seo_copy_content.seo_copy_content',
  'shelfresultbottom.year_range_for_makemodel_by_model.year_range_for_makemodel_by_model',
  'shelfresultbottom.pagination_style_links.pagination_style_links',
  'shelfresultbottom.faq.faq',
  'shelfresultbottom.image_category_grid.image_category_grid',
  'shelfresultbottom.product_chart.product_chart',
  'shelfresultbottom.product_grid.product_grid',
  'shelfresultbottom.category_grid.category_grid',
  'shelfresultbottom.blogtype_grid.blogtype_grid',
  'prefooter.prefooter',
  'shelfpagebody.pencil_banner.pencil_banner',
];

export const getProductListingPageCacheKey = (userSegments?: string[]) => {
  const segmentsKey = userSegments?.sort();
  const key: Array<string | string[]> = ['plp', 'response'];

  if (segmentsKey) {
    key.push(segmentsKey);
  }

  return key;
};

export const getContentStackPLPContent = async (
  req: NextApiRequest | PageContextType['req'],
  query: ParsedUrlQuery,
  userSegments: string[],
): Promise<FormattedContentStackPLP> => {
  const response = await getContentByContentType<ContentStackProductListingPage>({
    contentTypeUid: 'product_listing_page',
    referenceFieldPath: [...PRODUCT_LISTING_PAGE_FIELDS],
    req,
    query,
    customizeQuery: ({ query, sdk, contentTypeUid, queryParams }) => {
      if ('url' in queryParams && queryParams.url !== '' && typeof queryParams.url === 'string') {
        if (queryParams.productListingPageType === 'Merch Shelf') {
          query.where('additional_attributes_merch_shelf_page.url', queryParams.url);
        } else if (queryParams.productListingPageType === 'Brand Shelf (Premium)') {
          query.where('additional_attributes_premium_brand_shelf.url', queryParams.url);
        } else if (queryParams.productListingPageType === 'Search Result') {
          const defaultSearchResult = sdk
            .ContentType(contentTypeUid)
            .Query()
            .where('additional_attributes_search_result_page.url', '');
          const filteredSearchResult = sdk
            .ContentType(contentTypeUid)
            .Query()
            .where('additional_attributes_search_result_page.url', queryParams.url);
          query
            .or(filteredSearchResult, defaultSearchResult)
            .descending('additional_attributes_search_result_page.url');
        }
      }

      if (
        'productListingPageType' in queryParams &&
        queryParams.productListingPageType !== '' &&
        typeof queryParams.productListingPageType === 'string'
      ) {
        query.where('page_type', queryParams.productListingPageType);
      }

      return query;
    },
  });

  let taxonomyPath = '';
  if (query.taxonomyPath) {
    taxonomyPath = Array.isArray(query.taxonomyPath)
      ? `/${query.taxonomyPath.join('/')}`
      : query.taxonomyPath;
  } else if (query.url) {
    taxonomyPath = Array.isArray(query.url) ? `/${query.url.join('/')}` : query.url;
  }
  const { shelfpagebody, shelfresultbottom, shelfresultheader, ...rest } = response;

  let shelfPageBody = shelfpagebody;
  let shelfResultHeader = shelfresultheader;
  let shelfResultBottom = shelfresultbottom;

  if (taxonomyPath) {
    shelfPageBody = deepFilterAndSegmentContent({
      item: shelfPageBody,
      taxonomyPath,
      userSegments,
      exactMatch: false,
    });
    shelfResultHeader = deepFilterAndSegmentContent({
      item: shelfResultHeader,
      taxonomyPath,
      userSegments,
      exactMatch: false,
    });
    shelfResultBottom = deepFilterAndSegmentContent({
      item: shelfResultBottom,
      taxonomyPath,
      userSegments,
      exactMatch: false,
    });
  }

  return {
    shelfpagebody: shelfPageBody,
    shelfresultheader: shelfResultHeader,
    shelfresultbottom: shelfResultBottom,
    ...rest,
  };
};
